import React, { useState, useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";

function Radio({ app, field }) {
  const { defaultOption, options } = field || {};
  const measure = field?.measure || "Analise_TOP";
  const [active, setActive] = useState(defaultOption || "");
  const inFlightRef = useRef(false); // evita alternância por corrida

  // Lê do Qlik qual valor está realmente selecionado no campo
  const readSelection = useCallback(async () => {
    if (!app || !measure) return;
    try {
      const data = await app.field(measure).getData();
      const rows = data?.rows || [];
      const sel = rows.find((r) => r.qState === "S");
      if (sel?.qText) setActive((sel.qText || "").trim());
    } catch {}
  }, [app, measure]);

  const handleChange = useCallback(async (raw) => {
    const value = (raw || "").trim();
    if (!app || !measure || !value) return;
    if (inFlightRef.current) return; // ignora cliques enquanto aplica seleção
    inFlightRef.current = true;

    try {
      const fld = app.field(measure);

      // 1) limpa seleções anteriores (evita toggle e estados transitórios)
      //    (se você não quiser limpar sempre, comente a linha abaixo)
      await fld.clear();

      // 2) aplica seleção determinística (toggle=false)
      //    (evite o 3º parâmetro "softlock" para não criar comportamento estranho)
      await fld.selectValues([value], false);

      // 3) lê o estado confirmado do engine e sincroniza o 'active'
      await readSelection();
    } catch (e) {
      // silencioso/log opcional
      // console.warn("Falha ao selecionar", e);
    } finally {
      inFlightRef.current = false;
    }
  }, [app, measure, readSelection]);

  // Inicialização: se já existe seleção, usa; senão, aplica o default uma única vez
  const initializeField = useCallback(async () => {
    if (!app || !measure) return;

    try {
      const data = await app.field(measure).getData();
      const rows = data?.rows || [];
      const sel = rows.find((r) => r.qState === "S");

      if (sel?.qText) {
        setActive((sel.qText || "").trim());
      } else if (defaultOption) {
        // aplica default de forma determinística (sem toggle)
        const def = (defaultOption || "").trim();
        const fld = app.field(measure);
        await fld.clear();
        await fld.selectValues([def], false);
        setActive(def);
      }
    } catch {}
  }, [app, measure, defaultOption]);

  // Assina mudanças globais de seleção para manter o componente sempre em sincronia
  useEffect(() => {
    if (!app || !measure) return;

    let detach = () => {};
    try {
      const selState = app.selectionState && app.selectionState();
      if (selState && selState.OnDataChanged && selState.OnDataChanged.add) {
        const handler = () => {
          // sempre que qualquer seleção global mudar,
          // sincroniza o estado 'active' com o valor real do campo
          readSelection();
        };
        selState.OnDataChanged.add(handler);
        detach = () => selState.OnDataChanged.remove(handler);
      }
    } catch {}

    // leitura inicial
    initializeField();

    // sempre que app/measure mudar, ler estado atual
    readSelection();

    return () => detach();
  }, [app, measure, initializeField, readSelection]);

  return (
    <div className="radio-filter-container">
      {(options || []).map((element) => {
        const name = (element?.name || "").trim();
        return (
          <div style={{ marginRight: "20px" }} key={element.id ?? name}>
            <label className="radio-label">
              {name}
              <input
                className="radio-filter-input"
                type="radio"
                value={name}
                id={`radio-${measure}-${name}`}
                name={`radio-${measure}`}
                checked={(active || "").trim() === name}
                onChange={(e) => handleChange(e.target.value)}
                disabled={inFlightRef.current} // evita spam de cliques durante a seleção
              />
              <span className="checkmark" />
            </label>
          </div>
        );
      })}
    </div>
  );
}

Radio.defaultProps = {
  app: null,
  field: {},
};

Radio.propTypes = {
  app: PropTypes.object,
  field: PropTypes.object,
};

export default Radio;
